import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Context Providers
import { ThemeProvider } from './contexts/ThemeContext';
import { SiteBrandingProvider } from './contexts/SiteBrandingContext';
import { ColorSchemeProvider } from './contexts/ColorSchemeContext';
import { LanguageProvider } from './contexts/LanguageContext';

// Eager load critical layout components
import TopBar from './components/TopBar';
import Header from './components/Header';
import Navbar from './components/Navbar';
import Breadcrumb from './components/Breadcrumb';
import Footer from './components/Footer';
import StatsSection from './components/StatsSection';
import ScrollToTop from './components/ScrollToTop';

// Eager load Home page
import Home from './pages/Home';

// Lazy load all other pages
const About = lazy(() => import('./pages/About'));
const PrincipalMessage = lazy(() => import('./pages/PrincipalMessage'));
const VisionMission = lazy(() => import('./pages/VisionMission'));
const SMC = lazy(() => import('./pages/SMC'));
const Faculty = lazy(() => import('./pages/Faculty'));
const History = lazy(() => import('./pages/History'));
const Academics = lazy(() => import('./pages/Academics'));
const AcademicCalendar = lazy(() => import('./pages/AcademicCalendar'));
const Subjects = lazy(() => import('./pages/Subjects'));
const Examination = lazy(() => import('./pages/Examination'));
const AcademicPolicies = lazy(() => import('./pages/AcademicPolicies'));
const Admissions = lazy(() => import('./pages/Admissions'));
const FeeStructure = lazy(() => import('./pages/FeeStructure'));
const Eligibility = lazy(() => import('./pages/Eligibility'));
const ApplyOnline = lazy(() => import('./pages/ApplyOnline'));
const AdmissionFAQs = lazy(() => import('./pages/AdmissionFAQs'));
const CampusTour = lazy(() => import('./pages/CampusTour'));
const Laboratories = lazy(() => import('./pages/Laboratories'));
const Library = lazy(() => import('./pages/Library'));
const SportsFacilities = lazy(() => import('./pages/SportsFacilities'));
const Transport = lazy(() => import('./pages/Transport'));
const Events = lazy(() => import('./pages/Events'));
const Clubs = lazy(() => import('./pages/Clubs'));
const Sports = lazy(() => import('./pages/Sports'));
const Cultural = lazy(() => import('./pages/Cultural'));
const StudentCouncil = lazy(() => import('./pages/StudentCouncil'));
const Achievements = lazy(() => import('./pages/Achievements'));
const News = lazy(() => import('./pages/News'));
const Notices = lazy(() => import('./pages/Notices'));
const Circulars = lazy(() => import('./pages/Circulars'));
const PressReleases = lazy(() => import('./pages/PressReleases'));
const Downloads = lazy(() => import('./pages/Downloads'));
const Gallery = lazy(() => import('./pages/Gallery'));
const VideoGallery = lazy(() => import('./pages/VideoGallery'));
const VirtualTour = lazy(() => import('./pages/VirtualTour'));
const EventsGallery = lazy(() => import('./pages/EventsGallery'));
const Contact = lazy(() => import('./pages/Contact'));
const Admin = lazy(() => import('./pages/Admin'));
const PublicDisclosure = lazy(() => import('./pages/PublicDisclosure'));
const NotFound = lazy(() => import('./pages/NotFound'));

// Loading component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
      <p className="text-gray-600 dark:text-gray-400">Loading...</p>
    </div>
  </div>
);

function App() {
  return (
    <LanguageProvider>
      <ThemeProvider>
        <SiteBrandingProvider>
          <ColorSchemeProvider>
            <Router>
              <div className="flex flex-col min-h-screen">
                <ScrollToTop />
                
                {/* Top Bar */}
                <TopBar />
                
                {/* Header */}
                <Header />
                
                {/* Navigation */}
                <Navbar />
                
                {/* Breadcrumb */}
                <Breadcrumb />
                
                {/* Main Content */}
                <main className="flex-1">
                  <Suspense fallback={<PageLoader />}>
                    <Routes>
                    <Route path="/" element={<Home />} />
                    
                    {/* About Us Routes */}
                    <Route path="/about" element={<About />} />
                    <Route path="/principal-message" element={<PrincipalMessage />} />
                    <Route path="/vision-mission" element={<VisionMission />} />
                    <Route path="/smc" element={<SMC />} />
                    <Route path="/faculty" element={<Faculty />} />
                    <Route path="/history" element={<History />} />
                    
                    {/* Academics Routes */}
                    <Route path="/academics" element={<Academics />} />
                    <Route path="/academic-calendar" element={<AcademicCalendar />} />
                    <Route path="/subjects" element={<Subjects />} />
                    <Route path="/examination" element={<Examination />} />
                    <Route path="/academic-policies" element={<AcademicPolicies />} />
                    
                    {/* Admissions Routes */}
                    <Route path="/admissions" element={<Admissions />} />
                    <Route path="/fee-structure" element={<FeeStructure />} />
                    <Route path="/eligibility" element={<Eligibility />} />
                    <Route path="/apply-online" element={<ApplyOnline />} />
                    <Route path="/admission-faqs" element={<AdmissionFAQs />} />
                    
                    {/* Infrastructure Routes */}
                    <Route path="/campus-tour" element={<CampusTour />} />
                    <Route path="/laboratories" element={<Laboratories />} />
                    <Route path="/library" element={<Library />} />
                    <Route path="/sports-facilities" element={<SportsFacilities />} />
                    <Route path="/transport" element={<Transport />} />
                    
                    {/* Student Life Routes */}
                    <Route path="/events" element={<Events />} />
                    <Route path="/clubs" element={<Clubs />} />
                    <Route path="/sports" element={<Sports />} />
                    <Route path="/cultural" element={<Cultural />} />
                    <Route path="/student-council" element={<StudentCouncil />} />
                    
                    {/* Achievements */}
                    <Route path="/achievements" element={<Achievements />} />
                    
                    {/* News & Updates Routes */}
                    <Route path="/news" element={<News />} />
                    <Route path="/notices" element={<Notices />} />
                    <Route path="/circulars" element={<Circulars />} />
                    <Route path="/press-releases" element={<PressReleases />} />
                    
                    {/* Downloads */}
                    <Route path="/downloads" element={<Downloads />} />
                    
                    {/* Gallery Routes */}
                    <Route path="/gallery" element={<Gallery />} />
                    <Route path="/video-gallery" element={<VideoGallery />} />
                    <Route path="/virtual-tour" element={<VirtualTour />} />
                    <Route path="/events-gallery" element={<EventsGallery />} />
                    
                    {/* Contact */}
                    <Route path="/contact" element={<Contact />} />
                    
                    {/* Admin Routes */}
                    <Route path="/admin" element={<Admin />} />
                    <Route path="/public-disclosure" element={<PublicDisclosure />} />
                    
                    {/* 404 Page */}
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                  </Suspense>
                </main>
                
                {/* Stats Section */}
                <StatsSection />
                
                {/* Footer */}
                <Footer />
                
                {/* Toast Notifications */}
                <ToastContainer
                  position="top-right"
                  autoClose={5000}
                  hideProgressBar={false}
                  newestOnTop={false}
                  closeOnClick
                  rtl={false}
                  pauseOnFocusLoss
                  draggable
                  pauseOnHover
                  theme="colored"
                />
              </div>
            </Router>
          </ColorSchemeProvider>
        </SiteBrandingProvider>
      </ThemeProvider>
    </LanguageProvider>
  );
}

export default App;